import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ValidationRuleDto } from "@/api/Validations";

interface ActiveValidationRulesSectionProps {
  validationRules: ValidationRuleDto[];
  isLoading: boolean;
  error: string | null;
}

export const ActiveValidationRulesSection: React.FC<ActiveValidationRulesSectionProps> = ({
  validationRules,
  isLoading,
  error,
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Active Validation Rules</CardTitle>
        <CardDescription>
          Validation rules currently configured for this fund ({validationRules.length} rules)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
            <span className="text-muted-foreground">Loading validation rules...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-md">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : validationRules.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
            <p>No validation rules configured for this fund</p>
          </div>
        ) : (
          <div className="space-y-3">
            {validationRules.slice(0, 5).map((rule, index) => (
              <div
                key={`${rule.validationRuleId}-${index}`}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => navigate("/app/fund-validation-rules")}
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{rule.ruleName}</div>
                  <div className="text-muted-foreground text-xs mt-1">
                    <div className="flex gap-4 flex-wrap">
                      <span><strong>Type:</strong> {rule.ruleType}</span>
                      <span><strong>Scope:</strong> {rule.scope}</span>
                      {(() => {
                        try {
                          if (rule.exceptionRule) {
                            const parsedRule = JSON.parse(rule.exceptionRule);
                            if (parsedRule.type) {
                              return <span><strong>Rule:</strong> {parsedRule.type}</span>;
                            }
                            if (parsedRule.value !== undefined) {
                              return <span><strong>Threshold:</strong> {parsedRule.value}</span>;
                            }
                          }
                        } catch {
                          // If JSON parsing fails, show raw value
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                <Badge variant={rule.disabled ? "secondary" : "outline"} className="text-xs">
                  {rule.disabled ? "Disabled" : "Active"}
                </Badge>
              </div>
            ))}
            {validationRules.length > 5 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate("/app/fund-validation-rules")}
                className="mt-2 p-0 h-auto"
              >
                View all {validationRules.length} rules
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 